// Spec 082 — o indicador PRÓPRIO da organização.
//
// O aproveitamento do cliente é um caso dele, não um conceito da plataforma. Uma definição é nome,
// unidade base, meta opcional e dois conjuntos de FRASES; cada frase é "a linha X produz | consome
// | refuga o produto Y", o mesmo vocabulário do contador de máquina (Spec 081).
//
// O valor NÃO é armazenado: congelá-lo criaria uma terceira verdade que diverge do histórico assim
// que uma perda é corrigida. Ele é calculado a cada leitura pelo leitor por papel.
import { z } from "zod";
import { UuidSchema } from "./common.js";
import { ProductionRoleSchema } from "./parameters-counter.js";

export const IndicatorTermSideSchema = z.enum(["NUMERATOR", "DENOMINATOR"]);
export type IndicatorTermSide = z.infer<typeof IndicatorTermSideSchema>;

export const IndicatorTermSchema = z
  .object({
    side: IndicatorTermSideSchema,
    lineId: UuidSchema,
    role: ProductionRoleSchema,
    productId: UuidSchema,
  })
  .strict();
export type IndicatorTerm = z.infer<typeof IndicatorTermSchema>;

export const IndicatorDefinitionRequestSchema = z
  .object({
    name: z.string().min(1).max(120),
    baseUnit: z.string().min(1).max(30),
    target: z.number().finite().positive().nullable().optional(),
    terms: z.array(IndicatorTermSchema).min(2),
  })
  .strict()
  .refine((d) => d.terms.some((t) => t.side === "NUMERATOR"), {
    message: "O indicador precisa de pelo menos um termo no numerador.",
    path: ["terms"],
  })
  .refine((d) => d.terms.some((t) => t.side === "DENOMINATOR"), {
    message: "O indicador precisa de pelo menos um termo no denominador.",
    path: ["terms"],
  });
export type IndicatorDefinitionRequest = z.infer<typeof IndicatorDefinitionRequestSchema>;

export const IndicatorDefinitionResponseSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  baseUnit: z.string(),
  target: z.number().finite().nullable(),
  terms: z.array(IndicatorTermSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type IndicatorDefinitionResponse = z.infer<typeof IndicatorDefinitionResponseSchema>;

export const IndicatorValueQuerySchema = z
  .object({ from: z.string().datetime(), to: z.string().datetime() })
  .refine((q) => q.to > q.from, { message: "O fim deve ser posterior ao início.", path: ["to"] });
export type IndicatorValueQuery = z.infer<typeof IndicatorValueQuerySchema>;

/** O `preview` recebe uma definição AINDA NÃO SALVA. É ele que faz o número aparecer (FR-029). */
export const IndicatorPreviewRequestSchema = z.object({
  definition: IndicatorDefinitionRequestSchema,
  from: z.string().datetime(),
  to: z.string().datetime(),
});
export type IndicatorPreviewRequest = z.infer<typeof IndicatorPreviewRequestSchema>;

export const IndicatorTermValueSchema = z.object({
  side: IndicatorTermSideSchema,
  lineId: UuidSchema,
  lineName: z.string(),
  productId: UuidSchema,
  productName: z.string(),
  role: ProductionRoleSchema,
  /** `null` = sem registro no período. Nunca zero por falta de dado. */
  quantity: z.number().finite().nullable(),
  /** Qual origem respondeu: contador de máquina ou nível da linha. Nunca as duas somadas. */
  source: z.enum(["machine", "run"]),
});
export type IndicatorTermValue = z.infer<typeof IndicatorTermValueSchema>;

export const IndicatorValueResponseSchema = z.object({
  /** Percentual. NÃO capado em 100: acima disso é consumo de estoque, sinal legítimo (FR-031). */
  value: z.number().finite().nullable(),
  numerator: z.number().finite().nullable(),
  denominator: z.number().finite().nullable(),
  baseUnit: z.string(),
  target: z.number().finite().nullable(),
  /**
   * `NO_BASE` — nenhum termo do denominador tem registro no período. A tela escreve "sem base"
   * (FR-030), nunca `0%`: zero afirmaria que a fábrica não aproveitou nada.
   * `ZERO_BASE` — há registro, mas a soma é zero. Motivo distinto porque a ação do gestor é outra.
   */
  reason: z.enum(["OK", "NO_BASE", "ZERO_BASE"]),
  terms: z.array(IndicatorTermValueSchema),
});
export type IndicatorValueResponse = z.infer<typeof IndicatorValueResponseSchema>;
